import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import { useState } from 'react'
import { PopupState } from 'material-ui-popup-state/hooks'
import { toast } from 'sonner'
import Toast from './Toast'
import { AxiosError } from 'axios'
import { useResetTasksMutation } from '@qc_task_list_view/hooks/useResetTasksMutation'
import ArrowTooltips from '@common/components/Tooltip'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextField } from '@common/components/Form/TextField'
import { HTTPValidationError } from '@common/config/api/client'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: 2,
  bgcolor: 'background.default',
  boxShadow: 8,
  padding: 1,
}

const ResetValidationMessage = 'Provide a reason why you need to reset the task'

const ResetValidationSchema = z.object({
  reason: z.string().min(1, ResetValidationMessage),
})

export default function ResetModal({
  task_id,
  pseudo_id,
  study_id,
  popupState,
}: {
  task_id: string
  pseudo_id: string
  study_id: string
  popupState: PopupState
}) {
  const [open, setOpen] = useState(false)
  const {
    control,
    watch,
    formState: { isValid },
    setError,
  } = useForm({ mode: 'onChange', defaultValues: { reason: '' }, resolver: zodResolver(ResetValidationSchema) })
  const reason = watch('reason')
  const handleClose = () => {
    setOpen(false)
    popupState.close()
  }
  const { mutateAsync: resetTask } = useResetTasksMutation()

  const handleResetTask = async (task_ids: string[], study_id: string, reason: string) => {
    if (isValid) {
      await resetTask({
        status_change_reason: reason,
        qc_task_ids: task_ids,
        study_id: study_id,
      })
        .then(() => {
          toast(<Toast message="Your request was sent successfully" variant="success" data-testid="success-toast" />)
          handleClose()
        })
        .catch((error: AxiosError<HTTPValidationError>) => {
          const errorMessage = Array.isArray(error.response?.data?.detail)
            ? error.response?.data?.detail.map((err) => err.msg).join(', ')
            : error.response?.data?.detail || error.message
          toast(<Toast message={errorMessage} variant="error" data-testid="error-toast" />)
        })
    } else {
      setError('reason', { message: ResetValidationMessage })
    }
  }

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1.5,
          alignItems: 'center',
        }}
        data-testid="reset-task-button"
        tabIndex={0}
        aria-label="Reset Task Button"
      >
        <Typography variant="body1">Reset</Typography>
        <ArrowTooltips message={'Resetting the task will delete all entered data and start an empty QC task in QC1.'} />
      </Box>
      <Modal
        open={open}
        data-testid="reset-modal"
        aria-labelledby="reset-modal-title"
        aria-describedby="reset-modal-description"
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              padding: 2,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.5,
              alignSelf: 'stretch',
              width: 600,
            }}
          >
            <Typography variant="h6" color="text.primary" id="reset-modal-title">
              {`Reset task №${pseudo_id}?`}
            </Typography>
            <Typography variant="body1" color="text.secondary" id="reset-modal-description">
              The QC work for this Visit Event will be cleared. Briefly explain why you want to reset this task.
            </Typography>
          </Box>
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
              padding: 2,
            }}
            noValidate
            autoComplete="off"
          >
            <TextField
              name="reason"
              control={control}
              id="outlined-multiline-static"
              label="Provide a reason"
              multiline
              rows={6}
              fullWidth
              onKeyDown={(e) => e.stopPropagation()}
              data-testid="reason-textfield"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              alignSelf: 'stretch',
              padding: 2,
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
              }}
              onClick={handleClose}
              data-testid="cancel-reset-task-button"
              aria-label="Cancel Reset Task"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
              onClick={() => handleResetTask([task_id], study_id, reason)}
              data-testid="confirm-reset-task-button"
              aria-label="Confirm Reset Task"
            >
              Reset task
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
